import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Stock } from "../types";
import { formatCurrency, turkishIncludes } from "../utils/formatters";
import { poFmtQty } from "../utils/purchasing";
import { firmHue } from "./FirmPicker";
import "./FirmPicker.css";
import "./OrderPicker.css";
import "../pages/PurchaseOrders.css";

/**
 * Stok seçici — sipariş satırı eklemek (çoklu, "Ekle") ya da bir satırın stoğunu değiştirmek (tekli) için.
 * Firma/sipariş seçicilerle aynı kabuk (fp-*). Modal veri çekmez; stok listesini sipariş modalı verir.
 * Çokluda EKLEME anlamı vardır: taslak her açılışta boş başlar; belgede zaten olan stoklar "Belgede" rozeti taşır.
 */
export interface StockPickerModalProps {
  open: boolean;
  onClose: () => void;
  stocks: Stock[];
  loading?: boolean;
  error?: string;
  onReload?: () => void;
  mode: "multi" | "single";
  /** Belgede zaten bulunan stok kimlikleri — yalnız rozet; önceden işaretlenmez. */
  onDocument: string[];
  /** Çoklu: işaretlenen stoklar işaretleme sırasıyla. Tekli: [seçilen]. */
  onApply: (picked: Stock[]) => void;
  supplierId?: string;
  supplierName?: string;
  /** Stok modülü lisanslıysa mevcut miktar ve "Kritik" çipi gösterilir. */
  showLevels: boolean;
  title?: string;
}

type Chip = "" | "supplier" | "low" | "doc";
const MAX_ROWS = 300;
let seq = 0;

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v5h-5" /></svg>,
};

const stockMark = (s: Stock) => (s.productCode || s.productName || "ST").replace(/[^0-9A-Za-zÇĞİÖŞÜçğıöşü]/g, "").slice(0, 2).toUpperCase() || "ST";
const isLow = (s: Stock) => s.minimumStock != null && s.currentStock <= s.minimumStock;
const matches = (s: Stock, q: string) => {
  const terms = q.trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const hay = [s.productCode, s.productName, s.barcode, s.color, s.size, s.category?.name, s.supplierName || s.supplier].filter(Boolean).map(String);
  return terms.every((t) => hay.some((h) => turkishIncludes(h, t)));
};

export const StockPickerModal: React.FC<StockPickerModalProps> = ({ open, onClose, stocks, loading, error, onReload, mode, onDocument, onApply, supplierId, supplierName, showLevels, title }) => {
  const multi = mode === "multi";
  const uid = useRef(`sk${++seq}`).current;
  const [q, setQ] = useState("");
  const [chip, setChip] = useState<Chip>("");
  const [draft, setDraft] = useState<string[]>([]); // işaretleme sırası korunur
  const [cursorKey, setCursorKey] = useState<string | null>(null);
  const [live, setLive] = useState("");
  const liveTimer = useRef<number | undefined>(undefined);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const chipTouched = useRef(false);
  const pickChip = (c: Chip) => { chipTouched.current = true; setChip(c); };

  const source = useMemo(() => stocks.filter((s) => s.isActive !== false), [stocks]);
  const supplierCount = useMemo(() => (supplierId ? source.filter((s) => s.supplierId === supplierId).length : 0), [source, supplierId]);
  const onDoc = useMemo(() => new Set(onDocument), [onDocument]);

  useEffect(() => {
    if (!open) return;
    setQ(""); setDraft([]); setCursorKey(null); setLive("");
    chipTouched.current = false;
    setChip(supplierCount > 0 ? "supplier" : ""); // tedarikçinin stokları varsa onlarla başla
    const t = window.setTimeout(() => searchRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Liste açılıştan sonra gelirse (ilk açılış) varsayılan süzgeç yine uygulanır; kullanıcı seçtiyse dokunulmaz
  useEffect(() => {
    if (open && !chipTouched.current && supplierCount > 0) setChip((c) => (c === "" ? "supplier" : c));
  }, [open, supplierCount]);
  useEffect(() => () => window.clearTimeout(liveTimer.current), []);

  const filtered = useMemo(() => {
    const base = source.filter((s) => {
      if (chip === "supplier" && s.supplierId !== supplierId) return false;
      if (chip === "low" && !isLow(s)) return false;
      if (chip === "doc" && !onDoc.has(s.id)) return false;
      return matches(s, q);
    });
    return base.sort((a, b) => {
      if (supplierId && chip !== "supplier") { // seçili tedarikçinin stokları önce
        const am = a.supplierId === supplierId ? 0 : 1, bm = b.supplierId === supplierId ? 0 : 1;
        if (am !== bm) return am - bm;
      }
      return a.productName.localeCompare(b.productName, "tr");
    });
  }, [source, chip, q, supplierId, onDoc]);
  const shown = filtered.slice(0, MAX_ROWS);
  const keys = useMemo(() => shown.map((s) => s.id), [shown]);
  const cursor = Math.max(0, cursorKey ? keys.indexOf(cursorKey) : 0);
  useEffect(() => { setCursorKey(keys[0] ?? null); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, chip]);
  useEffect(() => { listRef.current?.querySelector<HTMLElement>(`[data-cursor="${cursor}"]`)?.scrollIntoView({ block: "nearest" }); }, [cursor]);

  const announce = (msg: string) => { setLive(msg); window.clearTimeout(liveTimer.current); liveTimer.current = window.setTimeout(() => setLive(""), 1500); };
  const refocus = () => { const el = searchRef.current; if (!el) return; el.focus(); if (el.value) el.select(); };

  const activate = (s: Stock) => {
    if (!multi) { onApply([s]); onClose(); return; }
    const add = !draft.includes(s.id);
    setDraft((d) => (add ? [...d, s.id] : d.filter((x) => x !== s.id)));
    announce(`${s.productCode || s.productName} ${add ? "eklendi" : "çıkarıldı"} · ${draft.length + (add ? 1 : -1)} seçili`);
    refocus();
  };
  const commit = () => {
    if (!multi || draft.length === 0) return;
    onApply(draft.map((id) => source.find((s) => s.id === id)).filter((s): s is Stock => !!s));
    onClose();
  };
  const resetFilters = () => { setQ(""); pickChip(""); refocus(); };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); onClose(); return; }
    if (multi && e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); commit(); return; }
    if (e.target !== searchRef.current) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursorKey(keys[Math.min(keys.length - 1, cursor + 1)] ?? null); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursorKey(keys[Math.max(0, cursor - 1)] ?? null); }
    else if (e.key === "Enter") { e.preventDefault(); const s = shown[cursor]; if (s) activate(s); }
  };

  if (!open) return null;
  const heading = title ?? (multi ? "Stok ekle" : "Stoğu değiştir");
  const sub = loading ? "Stoklar yükleniyor…" : `${supplierName ? supplierName + " · " : ""}${source.length} stok${multi ? ` · ${draft.length} seçili` : ""}`;

  return createPortal(
    <div className="fp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} onClick={(e) => e.stopPropagation()} onKeyDown={onKey}>
      <div className={`fp-modal ${multi ? "multi" : ""} po-pk-modal`} role="dialog" aria-modal="true" aria-labelledby={`${uid}-t`} onMouseDown={(e) => e.stopPropagation()}>
        <header className="fp-head">
          <div>
            <span className="fp-kicker">Stok</span>
            <h2 id={`${uid}-t`}>{heading}</h2>
            <p className="fp-sub">{sub}</p>
          </div>
          <button type="button" className="fp-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </header>
        <div className="fp-tools">
          <div className="fp-search">{I.search}
            <input ref={searchRef} placeholder="Kod, ad, barkod, renk veya kategori ara" value={q} onChange={(e) => setQ(e.target.value)} />
            {q && <button type="button" className="fp-ib" aria-label="Temizle" onClick={() => { setQ(""); searchRef.current?.focus(); }}>{I.close}</button>}
          </div>
          <div className="fp-chips">
            <button type="button" className={chip === "" ? "on" : ""} onClick={() => pickChip("")}>Tümü</button>
            {supplierCount > 0 && <button type="button" className={chip === "supplier" ? "on" : ""} onClick={() => pickChip("supplier")}>Bu tedarikçinin</button>}
            {showLevels && <button type="button" className={chip === "low" ? "on" : ""} onClick={() => pickChip("low")}>Kritik</button>}
            {onDocument.length > 0 && <button type="button" className={chip === "doc" ? "on" : ""} onClick={() => pickChip("doc")}>Belgede</button>}
          </div>
          {onReload && <button type="button" className="fp-btn" onClick={onReload} disabled={loading}>{I.refresh} Yenile</button>}
        </div>
        <div className="fp-list" ref={listRef} role="listbox" aria-label="Stoklar" aria-multiselectable={multi || undefined}>
          {error && <div className="fp-blank"><b>{error}</b>{onReload && <button type="button" className="fp-btn" onClick={onReload}>Yeniden dene</button>}</div>}
          {!error && loading && shown.length === 0 && <div className="fp-blank"><b>Stoklar yükleniyor…</b></div>}
          {!error && !loading && shown.length === 0 && (
            source.length === 0
              ? <div className="fp-blank"><span className="fp-blank-ico">{I.inbox}</span><b>Stok kartı bulunamadı</b>Sipariş satırı için önce stok kartı açılmalı.</div>
              : <div className="fp-blank"><span className="fp-blank-ico">{I.inbox}</span><b>{q ? `"${q}" için stok bulunamadı` : "Süzgece uyan stok yok"}</b>Kod, ad ya da barkod ile arayın.<button type="button" className="fp-btn" onClick={resetFilters}>Süzgeci sıfırla</button></div>
          )}
          {!error && shown.map((s, i) => {
            const checked = draft.includes(s.id); const low = isLow(s);
            return (
              <button type="button" key={s.id} role="option" aria-selected={checked} tabIndex={-1} className={`fp-row ${cursor === i ? "cur" : ""} ${checked ? "sel" : ""}`} data-cursor={i} onMouseEnter={() => setCursorKey(s.id)} onClick={() => activate(s)}>
                <span className="fp-mark" style={{ ["--fp-hue" as any]: firmHue(s.productCode || s.productName) }}>{stockMark(s)}</span>
                <span className="fp-text">
                  <b>{s.productName}{s.productCode && <span className="fp-code">{s.productCode}</span>}{onDoc.has(s.id) && <span className="fp-badge off">Belgede</span>}</b>
                  <small>{[s.category?.name, s.color, s.size, s.supplierName || s.supplier].filter(Boolean).join(" · ") || "—"}</small>
                </span>
                <span className="fp-side op-side po-pk-side">
                  {showLevels
                    ? <><b className={`po-pk-level ${low ? "low" : ""}`}>Mevcut {poFmtQty(s.currentStock)} {s.unit}</b><small>{s.unitPrice ? `Son ${formatCurrency(s.unitPrice, s.currency || "TRY")}` : "fiyat yok"}</small></>
                    : <><b>{s.unitPrice ? formatCurrency(s.unitPrice, s.currency || "TRY") : "fiyat yok"}</b><small>{s.unit}</small></>}
                </span>
                {multi && <span className={`fp-check ${checked ? "" : "empty"}`} aria-hidden="true">{I.check}</span>}
              </button>
            );
          })}
          {!error && filtered.length > MAX_ROWS && <div className="fp-count">{MAX_ROWS} / {filtered.length} stok — aramayı daraltın</div>}
          {!error && filtered.length <= MAX_ROWS && shown.length > 0 && !!q && filtered.length !== source.length && <div className="fp-count">{filtered.length} / {source.length} stok</div>}
        </div>
        <footer className="fp-foot">
          {multi
            ? <span className="fp-keys"><kbd>↑</kbd><kbd>↓</kbd> gezin · <kbd>Enter</kbd> işaretle · <kbd>Ctrl</kbd>+<kbd>Enter</kbd> ekle · <kbd>Esc</kbd> vazgeç</span>
            : <span className="fp-keys"><kbd>↑</kbd><kbd>↓</kbd> gezin · <kbd>Enter</kbd> seç · <kbd>Esc</kbd> vazgeç</span>}
          <span className="fp-actions">
            {multi && draft.length > 0 && <button type="button" className="fp-btn danger" onClick={() => { setDraft([]); announce("Seçim temizlendi"); refocus(); }}>Temizle</button>}
            <button type="button" className="fp-btn" onClick={onClose}>Vazgeç</button>
            {multi && <button type="button" className="fp-btn primary" onClick={commit} disabled={draft.length === 0}>{draft.length ? `Ekle · ${draft.length}` : "Ekle"}</button>}
          </span>
        </footer>
        <div className="fp-sr" role="status" aria-live="polite" aria-atomic="true">{live}</div>
      </div>
    </div>,
    document.body,
  );
};

export default StockPickerModal;
